import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import HeaderLayout from 'imports/job-tracking/ui/layout/HeaderLayout';
import styled, { css } from 'styled-components';

import ContactUsInfo from '/imports/core/ui/components/ContactUsInfo';
import ContainerAtom from '/imports/core/ui/atoms/Container';
import Footer from '/imports/core/ui/components/Footer';
import GeneralProvider from '/imports/job-tracking/api/context/general.context';
import Header from '/imports/dashboard/ui/components/Header';
import { isJobTrackEnable } from '/lib/helpers';
import SearchProvider from '/imports/job-tracking/api/context/search.context';
import useIntl from '/imports/core/api/useIntl';
import useMountedState from '/imports/core/api/useMountedState';
import { useResponsive } from '/imports/core/api/responsiveContext';
import useTracking from '/imports/core/hooks/useTracking';
import withAccountInfo from '/imports/core/api/accounts/withAccountInfo';

const ContactUs = dynamic(() => import('/imports/core/ui/components/ContactUs'), { ssr: false });

const ContactUsPage = ({ currentUser }) => {
  const { renderIfMounted } = useMountedState();
  const { t } = useIntl();
  const { host, isMobile } = useResponsive();
  const { trackEvent } = useTracking();
  const isJobTrackHeader = isJobTrackEnable() && !isMobile;
  useEffect(() => {
    trackEvent('contact-view', {}, true);
  }, []);

  const getComment = () => {
    switch (host) {
      case 'mysmartcv':
        return t('contact.mysmartcv_comments');
      default:
        return t('contact.comments');
    }
  };

  const contactUsComp = (isHeader, currentUser) => (
    <ContentWrapper isJobTrack isJobTrackHeader={isHeader}>
      <Content isJobTrack>
        <Container>
          <Heading isJobTrack>
            <FormHeading isJobTrack>{t('header.contact_us')}</FormHeading>
            <p>{getComment()}</p>
          </Heading>
          <ContactsInner isJobTrack>
            <ContactUs currentUser={currentUser} isJobTrack />
            <ContactUsInfo />
          </ContactsInner>
        </Container>
      </Content>
      <Footer isContactUsPage />
    </ContentWrapper>
  );

  const renderComponent = (currentUser) => {
    return <HeaderLayout>{contactUsComp(true, currentUser)}</HeaderLayout>;
  };

  function renderContactLayout(currentUser) {
    if (isJobTrackHeader) {
      return (
        <GeneralProvider>
          <SearchProvider>{renderComponent(currentUser)}</SearchProvider>
        </GeneralProvider>
      );
    } else {
      return (
        <ContentWrapper>
          <Header showDashboard gilroy loginModal />
          <Content>
            <Container>
              <Heading>
                <FormHeading>{t('header.contact_us')}</FormHeading>
                <p>{getComment()}</p>
              </Heading>
              <ContactsInner>
                <ContactUs currentUser={currentUser} />
                <ContactUsInfo />
              </ContactsInner>
            </Container>
          </Content>
          <Footer isContactUsPage />
        </ContentWrapper>
      );
    }
  }
  return renderIfMounted(
    <React.Fragment>
      {(host === 'cvdeboss' || host === 'modeles-cv') && (
        <Head>
          <title>Contactez-nous</title>
          <meta name="description" content="Voulez-vous nous faire part de commentaires ou de questions ?" />
        </Head>
      )}
      {renderContactLayout(currentUser)}
    </React.Fragment>,
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  transition: filter 0.2s;
  font-family: ${({ theme }) => theme.font.family.websiteRegular};
  font-size: 1.6rem;
  line-height: 1.35;
  font-weight: 400;
  ${({ theme }) =>
    !theme.isJobTrack &&
    theme.max('md')`
    margin-top: ${theme.general.headerHeight};
  `}
  ${({ isJobTrack }) =>
    isJobTrack &&
    css`
      max-width: 90%;
      width: auto;
      margin: 0 auto;
      @media (min-width: 768px) {
        max-width: 90%;
        width: 100%;
      }
    `}
  ${({ isJobTrackHeader }) =>
    isJobTrackHeader &&
    css`
      @media (min-width: 768px) {
        max-width: 100%;
      }
    `}
`;

const FormHeading = styled.h1`
  margin: -10px 0 0;
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 50px;
  font-weight: bold;
  color: #1688fe;
  ${(p) => p.theme.max('md')`
    font-size: 30px;
  `}
  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}
  ${({ isJobTrack }) =>
    isJobTrack &&
    css`
      font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
      color: var(--dark-values-black);
      font-size: 24px;
      line-height: 30px;
      margin: 0;
    `}
`;

const Content = styled.div`
  flex-grow: 1;
  position: relative;
  width: 100%;
  margin: 0 auto;
  background: #f7f7fc;
  ${({ isJobTrack }) =>
    isJobTrack &&
    css`
      background-color: #fff;
      margin: 0;
    `};
`;

const Container = styled(ContainerAtom)`
  display: flex;
  flex-direction: column;
  padding: 80px 0 60px;
  width: auto;
  max-width: ${(p) => p.theme.general.containerWidth.lg};
  margin: 0 auto;
  padding: 80px 32px 60px;
  ${(p) => p.theme.min('lg')`
    width: auto;
  `}
  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}
  @media(max-width:980px) {
    padding: 80px 0px 60px;
  }
  ${({ isJobTrack }) =>
    isJobTrack &&
    css`
      width: 100% !important;
      margin: 0;
      padding: 48px 0 0 0;
    `};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 34px;
  p {
    margin-top: 2px;
    font-family: ${({ theme }) => theme.font.family.websiteMedium};
    font-size: 20px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.3;
    letter-spacing: normal;
    color: #7171a6;
    margin-bottom: 0;
    ${(p) => p.theme.max('md')`
      font-size: 16px;
    `}
    ${({ theme: { isRTL } }) =>
      isRTL &&
      css`
        direction: rtl;
      `}
  }
  ${(p) => p.theme.max('md')`
    margin: 0 20px 34px;
  `}
  ${({ isJobTrack }) =>
    isJobTrack &&
    css`
      gap: 8px;
      margin-bottom: 32px;
      p {
        margin: 0;
        font-size: 15px;
        line-height: 22px;
        color: var(--dark-values-dark-regular);
      }
      ${(p) => p.theme.max('md')`
        margin: 0 0 25px 0;
      `}
    `}
`;

const ContactsInner = styled.div`
  display: flex;
  > div:nth-child(1) {
    flex-grow: 1;
  }
  > div:nth-child(2) {
    flex-shrink: 0;
  }
  ${(p) => p.theme.max('md')`
    flex-direction: column;
  `}
  ${({ isJobTrack }) =>
    isJobTrack &&
    css`
      width: 100%;
    `}
`;

ContactUsPage.propTypes = {
  currentUser: PropTypes.object,
};

export default withAccountInfo(ContactUsPage);
